import { useState } from 'react';
import { validateEmail, validatePhone, validateName, hasErrors } from '../utils/validation';
import { formatSlovakPhone } from '../utils/phoneFormatter';
import AddressAutocompleteField from './AddressAutocompleteField';
import './ReservationForm.css';

const ReservationForm = ({
  onSubmit,
  onBack,
  initialData = {},
  loading = false,
  emailReadOnly = false
}) => {
  const [formData, setFormData] = useState({
    cele_meno: initialData?.cele_meno || '',
    email: initialData?.email || '',
    telefon: initialData?.telefon || '',
    adresa: initialData?.adresa || '',
    poznamka: initialData?.poznamka || '',
    gdpr_suhlas: initialData?.gdpr_suhlas || false,
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateField = (fieldName) => {
    const value = formData[fieldName];
    let error = null;

    switch (fieldName) {
      case 'cele_meno':
        if (!value) {
          error = 'Meno je povinné';
        } else if (!validateName(value)) {
          error = 'Neplatné meno (min. 2 znaky, len písmená)';
        }
        break;
      
      case 'email':
        if (!value) {
          error = 'Email je povinný';
        } else if (!validateEmail(value)) {
          error = 'Neplatný email';
        }
        break;
      
      case 'telefon':
        if (!value) {
          error = 'Telefón je povinný';
        } else if (!validatePhone(value)) {
          error = 'Neplatné telefónne číslo';
        }
        break;
      
      case 'adresa':
        if (!value) {
          error = 'Adresa je povinná';
        } else if (value.length < 5) {
          error = 'Adresa musí mať aspoň 5 znakov';
        }
        break;
      
      case 'gdpr_suhlas':
        if (!formData.gdpr_suhlas) {
          error = 'Musíte súhlasiť so spracovaním osobných údajov';
        }
        break;
      
      default:
        break;
    }

    if (error) {
      setErrors(prev => ({ ...prev, [fieldName]: error }));
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }

    return !error;
  };

  const validateForm = () => {
    const requiredFields = ['cele_meno', 'email', 'telefon', 'adresa', 'gdpr_suhlas'];
    let isValid = true;

    requiredFields.forEach(field => {
      if (!validateField(field)) {
        isValid = false;
      }
    });

    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setTouched({
      cele_meno: true,
      email: true,
      telefon: true,
      gdpr_suhlas: true,
      adresa: true
    });

    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="reservation-form">
      <div className="form-grid-modern">
        {/* Celé meno */}
        <div className={`form-group-floating ${formData.cele_meno ? 'has-value' : ''} ${touched.cele_meno && errors.cele_meno ? 'has-error' : ''}`}>
          <input
            type="text"
            name="cele_meno"
            id="cele_meno"
            className="form-input-floating"
            value={formData.cele_meno}
            onChange={handleChange}
            placeholder=" "
            required
          />
          <label htmlFor="cele_meno" className="form-label-floating">Celé meno</label>
          {touched.cele_meno && errors.cele_meno && (
            <span className="field-error">{errors.cele_meno}</span>
          )}
        </div>

        {/* Email */}
        <div className={`form-group-floating ${formData.email ? 'has-value' : ''} ${touched.email && errors.email ? 'has-error' : ''}`}>
          <input
            type="email"
            name="email"
            id="email"
            className="form-input-floating"
            value={formData.email}
            onChange={handleChange}
            placeholder=" "
            required
            readOnly={emailReadOnly}
          />
          <label htmlFor="email" className="form-label-floating">Email</label>
          {touched.email && errors.email && (
            <span className="field-error">{errors.email}</span>
          )}
        </div>

        {/* Telefón */}
        <div className={`form-group-floating ${formData.telefon ? 'has-value' : ''} ${touched.telefon && errors.telefon ? 'has-error' : ''}`}>
          <input
            type="tel"
            name="telefon"
            id="telefon"
            className="form-input-floating"
            value={formData.telefon}
            onChange={handleChange}
            onBlur={(e) => {
              if (e.target.value) {
                const formatted = formatSlovakPhone(e.target.value);
                setFormData(prev => ({ ...prev, telefon: formatted }));
              }
            }}
            placeholder=" "
            required
          />
          <label htmlFor="telefon" className="form-label-floating">Telefón</label>
          {touched.telefon && errors.telefon && (
            <span className="field-error">{errors.telefon}</span>
          )}
        </div>

        {/* Adresa */}
        <AddressAutocompleteField
          value={formData.adresa}
          onChange={handleChange}
          name="adresa"
          id="adresa"
          label="Adresa"
          required
          hasError={Boolean(touched.adresa && errors.adresa)}
          errorMessage={touched.adresa ? errors.adresa : ''}
        />

        {/* Poznámka */}
        <div className={`form-group-floating full-width ${formData.poznamka ? 'has-value' : ''}`}>
          <textarea
            name="poznamka"
            id="poznamka"
            className="form-input-floating"
            rows="3"
            value={formData.poznamka}
            onChange={handleChange}
            placeholder=" "
            style={{ resize: "none" }}
          />
          <label htmlFor="poznamka" className="form-label-floating">Poznámka <span className="optional">(voliteľné)</span></label>
        </div>

        {/* GDPR súhlas */}
        <div className="form-group-floating full-width">
          <label className="form-checkbox">
            <input
              type="checkbox"
              name="gdpr_suhlas"
              checked={formData.gdpr_suhlas}
              onChange={handleChange}
            />
            <span>Súhlasím so spracovaním osobných údajov</span>
          </label>
          {touched.gdpr_suhlas && errors.gdpr_suhlas && (
            <span className="field-error">{errors.gdpr_suhlas}</span>
          )}
        </div>
      </div>

      <div className="modal-footer">
        {onBack && (
          <button type="button" onClick={onBack} className="btn btn-secondary">
            Späť
          </button>
        )}
        <button
          type="submit"
          disabled={loading || hasErrors(errors)}
          className="btn btn-primary"
        >
          {loading ? 'Spracúvam...' : 'Pokračovať'}
        </button>
      </div>
    </form>
  );
};

export default ReservationForm;
